<template>
    <element-wrapper
        :id="collection.id"
        kind="collection"
        :is_child="parent_id"
        :draggable="true"
        ref="instance"
        icon="fas fa-folder"
        @open="open"
        @moved="moved"
        @click-outside="stop_renaming">

        <span v-if="!collection.is_renaming">{{collection.name}}</span>

        <input v-else v-model="collection.name" @keyup.enter="rename" @click.stop=""
               name="name" type="text" v-validate="'required'" class="name-input"
               ref="name" @focus="$event.target.select()"/>

        <span v-if="errors.has('name')" class="error"><br/>{{errors.first('name')}}</span>

        <template slot="action">
            <i class="icon arrow fas" :class="is_open() ? 'fa-angle-down' : 'fa-angle-left'"></i>
        </template>

        <template slot="dropdown">
            <a v-if="space_topic_permission('COLLECTION_CREATE', topic)"
               @click="new_collection"
               class="dropdown-item">
                <i class="icon fas fa-folder-plus"></i> New Collection
            </a>

            <a v-if="space_topic_permission('NOTE_CREATE', topic)"
               @click="new_note"
               class="dropdown-item">
                <i class="icon fas fa-file-medical"></i> New Note
            </a>

            <a v-if="space_topic_permission('COLLECTION_UPDATE', topic)"
               @click="start_renaming"
               class="dropdown-item">
                <i class="icon fas fa-pen"></i> Rename
            </a>

            <a v-if="space_topic_permission('COLLECTION_DELETE', topic)"
               @click="show_confirm = true"
               class="dropdown-item">
                <i class="icon fas fa-trash-alt"></i> Delete
            </a>
        </template>

        <template slot="end" v-if="is_open()">
            <template v-for="item in tree_structure(collection.id)">
                <tree-collection
                    v-if="item.kind === 'collection'"
                    :key="item.kind + item.id"
                    :parent_id="collection.id"
                    :collection="item"
                    :space="space"
                    :topic="topic"
                    :is_private="is_private"
                    @renamed="$emit('renamed')"
                    @new-collection="$emit('new-collection', arguments[0])"
                    @new-note="$emit('new-note', arguments[0])"/>

                <tree-note
                    v-else-if="item.kind === 'note'"
                    :key="item.kind + item.id"
                    :parent_id="collection.id"
                    :space="space" :note="item"/>
            </template>
        </template>

        <base-confirm-modal
            v-if="show_confirm"
            text="Do you really want to remove this Collection?"
            @close="show_confirm = false"
            @ok="remove"/>
    </element-wrapper>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import TreeNote from './TreeNote.vue';
import ElementWrapper from '../common/ElementWrapper.vue';

export default {
    name: 'TreeCollection',
    components: { ElementWrapper, TreeNote },
    props: ['space', 'topic', 'collection', 'parent_id', 'is_private'],
    data() {
        return { show_confirm: false };
    },
    computed: {
        ...mapGetters('tree', ['tree_structure']),
        ...mapState('tree', ['open_collections']),
    },
    watch: {
        'collection.is_renaming': {
            immediate: true,
            handler(value) {
                if (value) this.$nextTick(() => this.$refs.name.focus());
            },
        },
    },
    methods: {
        is_open() {
            return this.open_collections.includes(this.collection.id);
        },
        open() {
            const open = this.is_open();
            this.switch_collection(!open);
            if (!open) {
                this.load();
            }
        },
        async load() {
            if (!this.collection.is_loaded) {
                await this.$store.dispatch('tree/list', {
                    space_id: this.space.id,
                    parents: [this.collection.id],
                    topic_id: this.topic ? this.topic.id : null,
                    is_private: this.is_private,
                });
            }
        },
        start_renaming() {
            this.$store.commit('tree/renaming_collection', {
                id: this.collection.id,
                is_renaming: true,
            });
            this.$refs.instance.close_dropdown();
        },
        stop_renaming() {
            if (!this.collection.is_renaming) return;
            this.$store.commit('tree/renaming_collection', { id: this.collection.id, is_renaming: false });
            this.name = this.collection.name;
        },
        async rename() {
            const result = await this.$validator.validate();
            if (!result) return;

            this.$store.commit('tree/renaming_collection', { id: this.collection.id, is_renaming: false });

            await this.$store.dispatch('tree/update_collection', {
                space_id: this.space.id,
                collection_id: this.collection.id,
                data: { name: this.collection.name },
            });

            this.$emit('renamed');
        },
        async new_collection() {
            this.$refs.instance.close_dropdown();
            this.switch_collection(true);
            await this.load();
            this.$emit('new-collection', this.collection.id);
        },
        async new_note() {
            this.$refs.instance.close_dropdown();
            this.switch_collection(true);
            await this.load();
            this.$emit('new-note', this.collection.id);
        },
        moved(moving_element) {
            const is_note = moving_element.kind === 'note';
            const is_collection = moving_element.kind === 'collection';
            const update_collection = this.space_topic_permission('COLLECTION_UPDATE', this.topic);
            const update_note = this.space_topic_permission('NOTE_UPDATE', this.topic);

            if (is_collection && !update_collection) return;
            if (is_note && !update_note) return;
            if (is_collection && moving_element.id === this.collection.id) return;

            this.$store.dispatch('tree/move_item', {
                id: moving_element.id,
                space_id: this.space.id,
                kind: moving_element.kind,
                move_to: this.collection.id,
            });
        },
        remove() {
            this.$store.dispatch('tree/remove_collection', {
                space_id: this.space.id,
                collection_id: this.collection.id,
            }).then(() => {
                // Reload parent collections children
                this.$store.commit('tree/remove_item', {
                    id: this.collection.id,
                    kind: 'collection',
                });

                this.$store.dispatch('tree/list', {
                    topic_id: this.topic.id,
                    parent_id: this.parent_id,
                    space_id: this.space.id,
                });
            });
        },
        switch_collection(open) {
            if (open) {
                this.$store.commit('tree/open_collection', {
                    collection_ids: [this.collection.id],
                });
            } else {
                this.$store.commit('tree/close_collection', {
                    collection_id: this.collection.id,
                });
            }
        },
    },
};
</script>

<style scoped>
    .name-input {
        background: transparent;
        border-width: 0 0 1px;
        border-color: gray;
        font-size: 1rem;
        color: #4a4a4a;
        outline: 0;
    }
    .arrow {
        float: right;
        margin-left: -10px;
    }
</style>
